import { useCallback, useEffect } from 'react';
import { API } from '../../API';
import Modal from '../Modal';
import * as d3 from 'd3';
import { useTranslation } from 'react-i18next';
import { useCurrentQuota } from '../../contexts/CurrentQuota';
import {ReactComponent as InfoIcon} from '../../assets/info.svg';
import { QuotaInfo } from './QuotaInfo';

import styles from './QuotaCheckModal.module.scss';

const formatNumberSignificant = d3.format('.3~s');
const formatWithCustomGigaBillion = (value) => {
  const formattedValue = formatNumberSignificant(value);
  const formattedWithBillion = formattedValue.replace('G', 'B');
  return formattedWithBillion;
};

export const QuotaCheckModal = ({
  isOpen,
  data,
  onClose,
  onChange,
  buttonText,
  redirect=true,
  isTopicModeling = false,
}) => {
  const { t } = useTranslation();
  const [currentQuota, setCurrentQuota] = useCurrentQuota();

  const getWorkspaceQuota = useCallback(() => {
    API.fetch('GET', `/API/v1/workspaces/current/quota`).then((data) => {
      setCurrentQuota(data);
    });
  }, []);
  
  useEffect(() => {
    getWorkspaceQuota();
  }, [getWorkspaceQuota]);

  const formQuotaData = (quotaType, data) => {
    if (quotaType === 'analytical') {
      return {
        is_significant_spend: data?.is_significant_analytical_spend,
        quota_usage: data?.analytical_quota_usage,
        can_create: data?.can_create,
        skip_confirmation: data?.skip_confirmation,
        quota_balance: currentQuota?.quota?.ANALYTICAL?.balance || 0,
        is_low_quota: currentQuota?.quota?.ANALYTICAL?.is_low
      } 
    } else if (quotaType === 'ai') {
      return {
        is_significant_spend: data?.is_significant_ai_spend,
        quota_usage: data?.ai_quota_usage,
        can_create: data?.can_create,
        skip_confirmation: data?.skip_confirmation,
        quota_balance: currentQuota?.quota?.AI?.balance || 0,
        is_low_quota: currentQuota?.quota?.AI?.is_low,
        ai_quota_usage_breakdown: data?.ai_quota_usage_breakdown || {}
      }
    }
  };

  return (
    <Modal
      isVisible={isOpen}
      title={t('Quota validation')}
      className={styles.quotaCheckingModal}
      content={
        <div>
          <div className={styles.quotaCheckModal}>
            <div className={styles.quotaCheckModalText}>
              {t('The current value is an estimate. The final quota will be calculated after the case is created and processed.')}
            </div>
            <div className={styles.quotaInfoWrapper}>
              <QuotaInfo quota={formQuotaData('analytical', data)} quotaType="analytical" />
              <QuotaInfo quota={formQuotaData('ai', data)} quotaType="ai" />
            </div>
            {isTopicModeling &&
            !data?.can_create &&
            data?.analytical_quota_usage > 20000 &&
            currentQuota?.quota?.ANALYTICAL?.balance > 0 ? (
              <div
                className={styles.quotaInfoMessage}
                style={{ color: '#FF4D4F' }}
              >
                <div className={styles.quotaInfoMessage_icon}>
                  <InfoIcon />
                </div>
                <span>
                  {t(
                    'The Topic Modeling (Beta) supports up to 20,000 publications per Case. Please refine your search to proceed.',
                  )}
                </span>
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
      }
      footer={
          <>
            <button
              className="btn-reset"
              onClick={() => {
                onClose();
              }}
            >
              {t('Cancel')}
            </button>
            <button
              className="btn-primary"
              disabled={!data?.can_create}
              onClick={(e) => {
                onChange(e);
                onClose();
              }}
            >
              {buttonText ? buttonText : t('Save')}
            </button>
          </>
      }
      onClose={() => onClose()}
    />
  );
};
